import {
  computed, onMounted, ref, type Ref,
} from 'vue'
import { format, parseISO } from 'date-fns'
import type { DoctorSlotsByDate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/types'
import getDateFnsLocaleByIso from 'src/functions/getDateFnsLocaleByIso'
import getDateItemsFromSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/functions/getDateItemsFromSlots'
import useContextStore from 'src/store/useContextStore'
import type { CommonContextStore } from 'src/types/context'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import apiLpuSpecialities from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/api/apiLpuSpecialities'
import type { LpuSpecialityOption } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/api/apiLpuSpecialities.types'
import apiLpuSlotsBySpeciality from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/api/apiLpuSlotsBySpeciality'
import { DAYS_FOR_DOCTOR_SLOTS } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/constants'
import redirectToAppointment from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/functions/redirectToAppointment'
import getTownTimedelta from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/getTownTimedelta'

export default function useLpuDoctorsSchedule(
  lpu: Ref<Lpu>,
  initialAppointmentType: Ref<'direct' | 'express' | null>,
) {
  const { context }: CommonContextStore = useContextStore()
  const specialities = ref<LpuSpecialityOption[]>([])
  const selectedSpeciality = ref<LpuSpecialityOption | null>(null)
  const slots = ref<DoctorSlotsByDate>({})
  const appointmentType = ref<'direct' | 'express' | null>(null)
  const activeDate = ref<string | null>(null)
  // true с старта — иначе SSR/первый paint показывают empty-state
  const isSpecialitiesLoading = ref(true)
  const isSlotsLoading = ref(false)
  const isFailed = ref(false)

  let slotsRequestId = 0

  const dayItems = computed(() => getDateItemsFromSlots(
    slots.value,
    getDateFnsLocaleByIso(context.current_language.iso),
  ))
  const activeDaySlots = computed(() => (
    activeDate.value ? (slots.value[activeDate.value] || []) : []
  ))
  const townTimedelta = computed(() => getTownTimedelta([
    context.town.timedelta,
    lpu.value.town.timedelta,
  ]))
  const isLoading = computed(() => (
    isSpecialitiesLoading.value || isSlotsLoading.value
  ))
  const isEmptySchedule = computed(() => (
    !isLoading.value
    && (!Object.keys(slots.value).length || dayItems.value.every(item => item.disabled))
  ))

  async function loadSlots(speciality: LpuSpecialityOption) {
    slotsRequestId += 1

    const requestId = slotsRequestId

    isSlotsLoading.value = true
    isFailed.value = false
    slots.value = {}
    appointmentType.value = null
    activeDate.value = null

    try {
      const { data } = await apiLpuSlotsBySpeciality.post({
        days: DAYS_FOR_DOCTOR_SLOTS,
        dt_start: format(new Date(), 'yyyy-MM-dd'),
        town_timedelta: townTimedelta.value,
        lpus_specialities: [{
          lpu_id: lpu.value.id,
          name: speciality.name,
          translit: speciality.translit,
          speciality_id: speciality.id,
        }],
      })

      if (requestId !== slotsRequestId) {
        return
      }

      const lpuSlots = data.result.find(item => item.lpu_id === lpu.value.id)
        || data.result[0]

      // eslint-disable-next-line require-atomic-updates
      slots.value = lpuSlots?.slots || {}
      appointmentType.value = lpuSlots?.appointment_type || null

      const daysWithSlots = Object.keys(slots.value).filter(day => slots.value[day].length)

      activeDate.value = dayItems.value.find(item => !item.disabled)?.date
        || daysWithSlots[0]
        || null
    } catch {
      if (requestId === slotsRequestId) {
        // eslint-disable-next-line require-atomic-updates
        isFailed.value = true
      }
    } finally {
      if (requestId === slotsRequestId) {
        // eslint-disable-next-line require-atomic-updates
        isSlotsLoading.value = false
      }
    }
  }

  async function loadSpecialities() {
    isSpecialitiesLoading.value = true
    isFailed.value = false

    try {
      const { data } = await apiLpuSpecialities.post([lpu.value.id])
      const list = data.specialities_by_lpu[lpu.value.id] || []

      // eslint-disable-next-line require-atomic-updates
      specialities.value = list

      if (list.length) {
        const [first] = list

        selectedSpeciality.value = first
        await loadSlots(first)
      }
    } catch {
      // eslint-disable-next-line require-atomic-updates
      isFailed.value = true
    } finally {
      // eslint-disable-next-line require-atomic-updates
      isSpecialitiesLoading.value = false
    }
  }

  async function selectSpeciality(speciality: LpuSpecialityOption) {
    if (selectedSpeciality.value?.id === speciality.id) {
      return
    }

    selectedSpeciality.value = speciality
    await loadSlots(speciality)
  }

  function selectDay(date: string) {
    activeDate.value = date
  }

  function onSlotClick(_event: Event, time: string, doctorId: number | undefined) {
    if (!selectedSpeciality.value || !activeDate.value || doctorId == null) {
      return
    }

    const type = appointmentType.value || initialAppointmentType.value

    // Express — запись по телефону: слоты только для ориентира, редирект на онлайн-запись не делаем
    if (type === 'express') {
      return
    }

    const redirectType = selectedSpeciality.value.is_in_club
      ? 'club'
      : 'default'

    redirectToAppointment({
      doctorId,
      lpuId: lpu.value.id,
      slot: `${format(parseISO(activeDate.value), 'dd.MM.yyyy')} ${time}`,
      specialityId: selectedSpeciality.value.id,
      type: redirectType,
    })
  }

  onMounted(loadSpecialities)

  return {
    specialities,
    selectedSpeciality,
    dayItems,
    activeDate,
    activeDaySlots,
    appointmentType,
    isLoading,
    isEmptySchedule,
    isSpecialitiesLoading,
    isSlotsLoading,
    isFailed,
    selectSpeciality,
    selectDay,
    onSlotClick,
  }
}
